import React, { FC, useState } from 'react';

import { tab } from '@testing-library/user-event/dist/tab';
import { theme as antTheme, Layout, Row, Space } from 'antd';

import { useTranslation } from 'react-i18next';

import { useSearchParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { useTableParams } from '@app/services/hooks/useTableParams';
import { HeaderTitle } from '@components/HeaderTitle';

import { Tabs as StyledTabs } from '@pages/SpaceSettings/styles';

import { ExportButton } from './components/ExportButton';
import { InviteModal } from './components/InviteModal';
import { InvitesTable } from './components/InvitesTable';
import { TableList } from './components/ListTable';
import { SearchControls } from './components/SearchControls';

export enum TABS {
  ALL = 'all',
  INVITE = 'invite',
}

export const EmployeesList: FC = () => {
  const [activeTab, setActiveTab] = useState(TABS.ALL);
  const currentSession = useLocalSelector((state) => state.currentSession);
  const employeesT = useTranslation(NSKeys.employees);
  const commonT = useTranslation(NSKeys.common);
  const [searchString, setSearchString] = useState('');
  const { token } = antTheme.useToken();
  const { current, pageSize, setPagination, resetParams } = useTableParams();
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const urlTab = params.get('tab');

    if (!urlTab) setSearchParams({ tab: activeTab });
    if (urlTab) setActiveTab(urlTab as TABS);
  }, []);

  const handleTabSelect = (tab: string) => {
    resetParams();
    setActiveTab(tab as TABS);
    setSearchParams({ tab });
  };

  const handleSearch = (value: string) => {
    setSearchString(value);
  };

  return (
    <Layout>
      <Layout.Header style={{ border: 0 }}>
        <Row justify="space-between" align="middle">
          <HeaderTitle title={employeesT.t('title')} />
          {currentSession?.data?.scope?.$permissions?.invite_users && (
            <Space size="middle">
              <InviteModal />

              <ExportButton params={{ name__contains: searchString }} />
            </Space>
          )}
        </Row>
      </Layout.Header>

      <Layout.Content>
        <StyledTabs
          destroyInactiveTabPane={true}
          activeKey={activeTab}
          defaultActiveKey={activeTab}
          items={[
            {
              key: TABS.ALL,
              label: employeesT.t('allTab'),
              children: (
                <TableList
                  setPagination={setPagination}
                  pageSize={pageSize}
                  current={current}
                  nameContains={searchString}
                />
              ),
            },
            {
              key: TABS.INVITE,
              label: employeesT.t('inviteTab'),
              children: <InvitesTable setPagination={setPagination} pageSize={pageSize} current={current} />,
            },
          ]}
          tabBarExtraContent={{
            right:
              activeTab === TABS.ALL ? (
                <SearchControls
                  isLoading={false}
                  placeholder={commonT.t('search')}
                  searchValue={searchString}
                  onSearchClick={handleSearch}
                />
              ) : null,
          }}
          onChange={handleTabSelect}
          $colorBgBase={token.colorBgBase}
        />
      </Layout.Content>
    </Layout>
  );
};
